import { computed, ref, Ref } from 'vue';
import { ComplexModal } from '@studyportals/modal';
import { ModalReviewUploadState } from '@/models/modal/modal.enum';
import { SurveyQuestion, SurveySubmissionAnswer } from '@studyportals/sp-lurch-interface';
import lurchClient from '@/infrastructure/lurch-client';
import rollbarOperator from '@/infrastructure/rollbar-operator';
import store from '@/store';

export default class ReviewUploadModalComponent {
	public trackingCompleted = ref(false);
	public modalContent: Ref<HTMLElement | null> = ref(null);
	public surveyVersion: Ref<number> = ref(0);
	private modal: Ref<ComplexModal | undefined> = ref(undefined);
	private surveyAnswers: Ref<SurveySubmissionAnswer[]> = ref([]);
	private surveyFileContentsMultipleOptionId: string | null = null;

	public firstBatchOfSurveyQuestions = computed((): SurveyQuestion[] => {
		return this.surveyQuestions.slice(0, 2);
	});

	public secondBatchOfSurveyQuestions = computed((): SurveyQuestion[] => {
		if (!this.isSelectedFileContentsMultiple.value) {
			return this.surveyQuestions.slice(2, 3);
		}

		return this.surveyQuestions.slice(2);
	});

	public isSelectedFileContentsMultiple = computed((): boolean => {
		return this.surveyAnswers.value.some(answer => answer.optionId === this.surveyFileContentsMultipleOptionId);
	});

	public isModalStepUploadScopeAndType = computed((): boolean => {
		return this.modalReviewUploadState.value === ModalReviewUploadState.UPLOAD_SCOPE_AND_TYPE;
	});

	public isModalStepUploadContents = computed((): boolean => {
		return this.modalReviewUploadState.value === ModalReviewUploadState.UPLOAD_CONTENTS;
	});

	public modalReviewUploadState = computed((): ModalReviewUploadState => {
		return store.getters.modalReviewUploadState();
	});

	private inputBatchIdentity = computed((): string => {
		return store.getters.inputBatchIdentity();
	});

	constructor(
		public surveyQuestions: SurveyQuestion[]
	) { }

	public async mounted(): Promise<void> {
		if (!this.surveyQuestions.length) {
			await this.closeModal();
			return;
		}

		this.trackingCompleted.value = false;

		this.setSurveyVersionNumber();
		await this.setSurveyFileContentsMultipleOptionId();

		this.prepareModal();
	}

	public unmounted(): void {
		if (!this.modal.value) {
			return;
		}

		window.ModalManager.close(this.modal.value);
	}

	public async closeModal(): Promise<void> {
		await this.wrapUpSurvey();
		store.mutations.setModalReviewUploadState(ModalReviewUploadState.INACTIVE);
	}

	public showUploadScopeAndTypeScreen(): void {
		store.mutations.setModalReviewUploadState(ModalReviewUploadState.UPLOAD_SCOPE_AND_TYPE);
	}

	public showUploadContentsScreen(): void {
		store.mutations.setModalReviewUploadState(ModalReviewUploadState.UPLOAD_CONTENTS);
	}

	public retrieveAnswerOfQuestion(questionId: string): SurveySubmissionAnswer | undefined {
		return this.surveyAnswers.value.find(answer => answer.questionId === questionId);
	}

	private setSurveyVersionNumber(): void {
		if (!this.surveyQuestions.length) {
			return;
		}

		this.surveyVersion.value = this.surveyQuestions[0].surveyVersion;
	}

	private async setSurveyFileContentsMultipleOptionId(): Promise<void> {
		const relevantOption = this.surveyQuestions[2]?.options[1];
		if (!relevantOption || !relevantOption.label.toLowerCase().includes('multiple')) {
			rollbarOperator.triggerError(
				'Post-upload survey structure is out of sync with expectations from front-end.',
				{ questions: this.surveyQuestions }
			);
			await this.closeModal();
			return;
		}

		this.surveyFileContentsMultipleOptionId = relevantOption.optionId;
	}


	private async wrapUpSurvey(): Promise<void> {
		// Ensure tracking is never handled twice (e.g. when the modal is closed in different ways) and no answerless survey is submitted.
		if (this.trackingCompleted.value || !this.surveyAnswers.value.length) {
			return;
		}

		try {
			await lurchClient.submitSurvey(
				this.inputBatchIdentity.value,
				{
					surveyVersion: this.surveyVersion.value,
					answers: this.surveyAnswers.value
				}
			);
		} catch (error) {
			rollbarOperator.triggerError('Failed to submit survey.', error);
		} finally {
			this.trackingCompleted.value = true;
		}
	}

	public storeQuestionAnswer(answer: SurveySubmissionAnswer): void {
		const existingId = this.surveyAnswers.value.findIndex(existingAnswer => existingAnswer.questionId === answer.questionId);
		if (existingId !== -1) {
			this.surveyAnswers.value[existingId] = answer;
			return;
		}

		this.surveyAnswers.value.push(answer);
	}

	private prepareModal(): void {
		if (!this.modalContent.value) {
			return;
		}

		this.modal.value = new ComplexModal(this.modalContent.value, {
			cssClassName: 'ReviewUploadModalWrapper',
			destroyOnClose: false,
			onClose: this.closeModal.bind(this)
		});

		window.ModalManager.open(this.modal.value);
	}
}
